import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, MapPin, User } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/p/Entrep%C3%B4t-Auto-Montr%C3%A9al-61577568119726/',
  linkedin: 'https://www.linkedin.com/company/entrep%C3%B4tautomontreal/'
};

export const Navbar = ({ transparent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [makes, setMakes] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchMakes();
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchMakes = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`);
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Error fetching makes:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Blue Top Bar */}
      <div className="relative z-50 bg-gradient-to-r from-[#1e40af] via-[#3b82f6] to-[#1e40af] text-white text-[10px] md:text-[11px] py-2.5 font-medium" style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top, 0px))' }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-6">
            <a href="https://maps.google.com/?q=1007+boul.+René-Lévesque,+Verdun" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-2 hover:text-white/80 transition-colors">
              <MapPin className="h-3.5 w-3.5" />
              <span>1007 boul. René-Lévesque, Verdun (Québec)</span>
            </a>
            <a href="tel:+18339956477" className="flex items-center gap-2 hover:text-white/80 transition-colors">
              <Phone className="h-3.5 w-3.5" />
              <span>(833) 995-6477</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <a 
                href={SOCIAL_LINKS.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                aria-label="Facebook"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href={SOCIAL_LINKS.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                aria-label="LinkedIn"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              {/* Language Switcher in top bar */}
              <LanguageSwitcher variant="topbar" />
            </div>
            {user ? (
              <Link 
                to="/admin" 
                className="hidden md:flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-white transition-colors"
                data-testid="nav-admin-link"
              >
                <User className="h-3.5 w-3.5" />
                <span>Admin</span>
              </Link>
            ) : (
              <Link 
                to="/login" 
                className="hidden md:flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-white transition-colors"
                data-testid="nav-client-area-topbar"
              >
                <User className="h-3.5 w-3.5" />
                <span>{t('nav.clientArea')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
      
      <nav className={`${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : transparent ? 'bg-transparent' : 'bg-white'} sticky top-0 z-50 transition-all duration-300`} data-testid="navbar">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0 mr-6" data-testid="nav-logo">
              <div className="relative flex-shrink-0">
                <img 
                  src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460833/entrepot/assets/company_logo_400.png" 
                  alt="Entrepôt Auto Montréal" 
                  className="h-12 w-auto transition-transform group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col">
                <span className={`text-base 2xl:text-lg font-bold leading-tight transition-colors whitespace-nowrap ${transparent && !scrolled ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Entrepôt Auto Montréal
                </span>
                <span className={`text-[9px] italic leading-tight ${transparent && !scrolled ? 'text-white/70' : 'text-slate-500'}`}>
                  Redéfinir l'expérience Automobile
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden min-[1360px]:flex items-center min-w-0" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {/* Nav Links Container with subtle background */}
              <div className="flex items-center gap-0.5 bg-slate-50/80 rounded-full px-1.5 py-1 border border-slate-100">
                <Link
                  to="/"
                  className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${
                    isActive('/') 
                      ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                      : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                  }`}
                  data-testid="nav-link-accueil"
                >
                  {t('nav.home')}
                </Link>
                
                {/* Inventaire with dropdown */}
                <div className="relative group">
                  <Link
                    to="/inventory"
                    className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors flex items-center gap-1 ${
                      isActive('/inventory') 
                        ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                        : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                    }`}
                    data-testid="nav-link-inventaire"
                  >
                    {t('nav.inventory')}
                    <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
                  </Link>
                  
                  {/* Dropdown */}
                  {makes.length > 0 && (
                    <div className="absolute top-full left-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 py-4 min-w-[400px] overflow-hidden">
                        {/* Header */}
                        <div className="px-5 pb-3 mb-3 border-b border-slate-100 flex items-center justify-between">
                          <span className="text-sm font-bold text-slate-800">{t('inventory.brand')}</span>
                          <Link
                            to="/inventory"
                            className="text-xs font-semibold text-[#3b82f6] hover:text-[#2563eb] flex items-center gap-1"
                          >
                            {t('inventory.viewAllBrands')}
                            <ChevronDown className="h-3 w-3 rotate-[-90deg]" />
                          </Link>
                        </div>
                        
                        {/* Brand Grid */}
                        <div className="grid grid-cols-4 gap-2 px-4 max-h-[280px] overflow-y-auto">
                          {makes.map((make) => (
                            <Link
                              key={make}
                              to={`/inventory?make=${encodeURIComponent(make)}`}
                              className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-gradient-to-br hover:from-[#3b82f6]/5 hover:to-[#3b82f6]/10 transition-all group/item"
                            >
                              <div className="w-12 h-12 rounded-full bg-slate-50 group-hover/item:bg-white group-hover/item:shadow-md flex items-center justify-center transition-all overflow-hidden border border-slate-100 group-hover/item:border-[#3b82f6]/20">
                                <img 
                                  src={`https://www.carlogos.org/car-logos/${make.toLowerCase().replace(' ', '-')}-logo.png`}
                                  alt={make}
                                  className="w-8 h-8 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <span className="hidden w-8 h-8 items-center justify-center text-sm font-bold text-[#3b82f6]">
                                  {make.charAt(0)}
                                </span>
                              </div>
                              <span className="text-xs font-medium text-slate-600 group-hover/item:text-[#3b82f6] text-center transition-colors">
                                {make}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <Link
                  to="/financement"
                  className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${
                    isActive('/financement') 
                      ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                      : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                  }`}
                  data-testid="nav-link-financement"
                >
                  {t('nav.financing')}
                </Link>
                
                <Link
                  to="/vendre"
                  className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${
                    isActive('/vendre') 
                      ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                      : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                  }`}
                  data-testid="nav-link-vendre"
                >
                  {t('nav.sell')}
                </Link>
                
                <Link
                  to="/nos-livraisons"
                  className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${
                    isActive('/nos-livraisons') 
                      ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                      : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                  }`}
                  data-testid="nav-link-livraisons"
                >
                  {t('nav.deliveries')}
                </Link>
                
                <Link
                  to="/reprise"
                  className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${
                    isActive('/reprise') 
                      ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200' 
                      : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                  }`}
                  data-testid="nav-link-reprise"
                >
                  {t('nav.tradein')}
                </Link>

                <div className="relative group">
                  <button
                    type="button"
                    className={`px-2.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap transition-colors flex items-center gap-1 ${
                      ['/equipe', '/blog', '/notre-processus', '/contact'].some(isActive)
                        ? 'bg-[#3b82f6] text-white shadow-md shadow-blue-200'
                        : `${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-white hover:shadow-sm'}`
                    }`}
                    data-testid="nav-more-button"
                  >
                    {t('nav.more')}
                    <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute top-full right-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-200">
                    <div className="bg-white rounded-xl shadow-2xl border border-slate-100 py-2 min-w-[200px]">
                      {[['/equipe', t('nav.team'), 'equipe'], ['/blog', 'Blog', 'blog'], ['/notre-processus', t('nav.processus'), 'processus'], ['/contact', t('nav.contact'), 'contact']].map(([to, label, id]) => (
                        <Link
                          key={to}
                          to={to}
                          className={`block px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors ${isActive(to) ? 'text-[#3b82f6] bg-blue-50' : 'text-slate-700 hover:bg-slate-50 hover:text-[#3b82f6]'}`}
                          data-testid={`nav-link-${id}`}
                        >
                          {label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - CTA */}
            <div className="hidden min-[1360px]:flex items-center gap-2 flex-shrink-0 ml-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <a 
                href="tel:+18339956477"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-[11px] uppercase tracking-[0.08em] whitespace-nowrap transition-colors border-2 ${
                  transparent && !scrolled 
                    ? 'text-white border-white/40 hover:bg-white hover:text-[#3b82f6]' 
                    : 'text-[#3b82f6] border-[#3b82f6]/30 bg-[#3b82f6]/5 hover:bg-[#3b82f6] hover:text-white hover:border-[#3b82f6]'
                }`}
              >
                <Phone className="h-3.5 w-3.5" />
                <span className="hidden 2xl:inline">{t('nav.callUs')}</span>
              </a>
              <Button 
                asChild 
                className="bg-gradient-to-r from-[#3b82f6] to-[#1d4ed8] hover:from-[#2563eb] hover:to-[#1e40af] rounded-full shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 hover:scale-105 transition-all px-4 py-2 font-bold text-[11px] uppercase tracking-[0.08em] whitespace-nowrap h-auto" 
                data-testid="nav-cta-button"
              >
                <Link to="/credit">{t('nav.freeCredit')}</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`min-[1360px]:hidden p-2 rounded-lg transition-colors ${transparent && !scrolled ? 'text-white hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'}`}
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="min-[1360px]:hidden pb-6 animate-in slide-in-from-top-2 duration-200" data-testid="mobile-menu">
              <div className="flex flex-col gap-1 py-4">
                <Link
                  to="/"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Accueil
                </Link>
                <Link
                  to="/inventory"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/inventory') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Inventaire
                </Link>
                <Link
                  to="/financement"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/financement') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Financement
                </Link>
                <Link
                  to="/vendre"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/vendre') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.sellYourVehicle')}
                </Link>
                <Link
                  to="/equipe"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/equipe') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Notre Équipe
                </Link>
                <Link
                  to="/contact"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/contact') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/blog"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/blog') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/nos-livraisons"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/nos-livraisons') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.deliveries')}
                </Link>
                <Link
                  to="/notre-processus"
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive('/notre-processus') ? 'bg-[#3b82f6] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.processus')}
                </Link>
                {user && (
                  <Link
                    to="/admin"
                    className="px-4 py-3 rounded-xl text-sm font-medium transition-all bg-slate-800 text-white hover:bg-slate-700"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-admin-link"
                  >
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Admin
                    </span>
                  </Link>
                )}
              </div>
              
              <div className="flex flex-col gap-3 pt-4 border-t border-slate-100">
                <a 
                  href="tel:+18339956477"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm text-slate-600 border border-slate-200 hover:border-[#3b82f6] hover:text-[#3b82f6] transition-all"
                >
                  <Phone className="h-4 w-4" />
                  <span>(833) 995-6477</span>
                </a>
                <Button asChild className="w-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] rounded-xl py-6 font-semibold">
                  <Link to="/credit" onClick={() => setIsOpen(false)}>{t('nav.freeCredit')}</Link>
                </Button>
              </div>
              
              {/* Mobile Social Icons */}
              <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-100">
                <a 
                  href={SOCIAL_LINKS.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center text-[#1877F2] transition-all"
                  aria-label="Facebook"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href={SOCIAL_LINKS.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 flex items-center justify-center text-[#0A66C2] transition-all"
                  aria-label="LinkedIn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
